/**
 * Pre-built invoice themes used by the live preview and the server-side PDF
 * renderer. Each theme defines a small set of CSS custom properties applied
 * to the preview container; the server-side PDF in `exporters.ts` has a
 * matching set of themes keyed by `id`.
 */

export interface InvoiceTheme {
  id: string;
  label: string;
  description: string;
  background: string;
  ink: string;
  mutedInk: string;
  rule: string;
  accent: string;        // overridden by per-invoice accentColor
  fontStack: string;
  fontTitleStack: string;
  headerLayout: 'left' | 'right' | 'center';
  bandStyle: 'solid' | 'outline' | 'minimal';
}

export const THEMES: InvoiceTheme[] = [
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'Épuré Helvetica, lignes discrètes.',
    background: '#FFFFFF',
    ink: '#1A1A1A',
    mutedInk: '#6E6E6E',
    rule: '#E5E5E5',
    accent: '#ed5c25',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'minimal',
  },
  {
    id: 'classic',
    label: 'Classique',
    description: 'Times-Roman centré, en-tête encadré.',
    background: '#FFFFFF',
    ink: '#101213',
    mutedInk: '#555555',
    rule: '#CFCFCF',
    accent: '#ed5c25',
    fontStack: "'Times New Roman', Georgia, 'Times', serif",
    fontTitleStack: "'Times New Roman', Georgia, 'Times', serif",
    headerLayout: 'center',
    bandStyle: 'outline',
  },
  {
    id: 'bold',
    label: 'Audacieux',
    description: 'Bandeau sombre plein, fort contraste.',
    background: '#FFFFFF',
    ink: '#0F1115',
    mutedInk: '#6B6F76',
    rule: '#1F2128',
    accent: '#1F2128',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'solid',
  },
  {
    id: 'mono',
    label: 'Mono',
    description: 'Typographie Courier, rendu technique.',
    background: '#FFFFFF',
    ink: '#1A1A1A',
    mutedInk: '#777777',
    rule: '#E2E2E2',
    accent: '#1A1A1A',
    fontStack: "'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace",
    fontTitleStack: "'JetBrains Mono', 'Fira Code', Menlo, Consolas, monospace",
    headerLayout: 'left',
    bandStyle: 'minimal',
  },
  {
    id: 'vibrant',
    label: 'Vibrant',
    description: 'Titre à droite, bandeau violet moderne.',
    background: '#FFFFFF',
    ink: '#101010',
    mutedInk: '#666666',
    rule: '#EAEAEA',
    accent: '#7C3AED',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'right',
    bandStyle: 'solid',
  },
  {
    id: 'slate',
    label: 'Ardoise',
    description: 'En-tête gris acier, style corporate & juridique.',
    background: '#FFFFFF',
    ink: '#13171B',
    mutedInk: '#5E6770',
    rule: '#C9D1D9',
    accent: '#37475A',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'outline',
  },
  {
    id: 'forest',
    label: 'Forêt',
    description: 'Accent vert émeraude, naturel et soigné.',
    background: '#FFFFFF',
    ink: '#0D1F18',
    mutedInk: '#587168',
    rule: '#DDEEDF',
    accent: '#1F6F4B',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'minimal',
  },
  {
    id: 'sunset',
    label: 'Coucher de soleil',
    description: 'Bande ambre chaleureuse, créatif.',
    background: '#FFFFFF',
    ink: '#241308',
    mutedInk: '#8A6450',
    rule: '#F1E4DA',
    accent: '#C9821E',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'solid',
  },
  {
    id: 'coral',
    label: 'Corail',
    description: 'Bande corail douce, chaleureux et accueillant.',
    background: '#FFFFFF',
    ink: '#1F1414',
    mutedInk: '#84605C',
    rule: '#F4DCD7',
    accent: '#E26B5C',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'center',
    bandStyle: 'minimal',
  },
  {
    id: 'crimson',
    label: 'Pourpre',
    description: 'Bandeau rouge intense, fort impact visuel.',
    background: '#FFFFFF',
    ink: '#190B0B',
    mutedInk: '#7A5556',
    rule: '#EFD9D8',
    accent: '#B33530',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'solid',
  },
  {
    id: 'ocean',
    label: 'Océan',
    description: 'Bleu sarcelle profond, calme et professionnel.',
    background: '#FFFFFF',
    ink: '#0A1A22',
    mutedInk: '#516C7A',
    rule: '#D5E4EA',
    accent: '#1B6F8A',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'right',
    bandStyle: 'outline',
  },
  {
    id: 'midnight',
    label: 'Nuit',
    description: 'Thème sombre inversé, très élégant.',
    background: '#0D1117',
    ink: '#E6EDF3',
    mutedInk: '#8B96A3',
    rule: '#232A33',
    accent: '#7AB5FF',
    fontStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    fontTitleStack: "system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
    headerLayout: 'left',
    bandStyle: 'minimal',
  },
];

export function getTheme(id: string): InvoiceTheme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

export const ACCENT_SWATCHES: string[] = [
  '#ed5c25', // Ma Facture orange
  '#3c6e71', // Deep Teal
  '#284b63', // Prussian Slate Blue
  '#353535', // Charcoal Ink
  '#14213d', // Oxford Navy
  '#e5b369', // Champagne Gold
  '#10b981', // Emerald Jade
  '#f59e0b', // Amber Topaz
  '#f43f5e', // Crimson Ruby
];
